import { V2_REMINDER_CHANNELS } from "./v2Schema";

export const DEFAULT_WHATSAPP_REMINDER_MESSAGE = "Reminder: it's time for your medication.";
export const WHATSAPP_SEND_STATUSES = Object.freeze({
  pending: "pending",
  sent: "sent",
  dryRun: "dry_run",
  failed: "failed",
  disabled: "disabled",
});
const DOSE_LIKE_PATTERN = /\b\d+(\.\d+)?\s*(mg|mcg|g|ml|iu|units?|tablets?|tabs?|capsules?|caps?|pills?|drops?)\b/gi;

export function normalizeWhatsAppNumber(value = "") {
  const trimmed = String(value || "").trim();
  const hasPlus = trimmed.startsWith("+");
  const digits = trimmed.replace(/[^\d]/g, "");
  return `${hasPlus ? "+" : ""}${digits}`;
}

export function validateWhatsAppNumber(value = "") {
  const normalized = normalizeWhatsAppNumber(value);
  const valid = /^\+?\d{8,15}$/.test(normalized);

  return {
    valid,
    number: normalized,
    error: valid ? "" : "Enter a valid WhatsApp number.",
  };
}

export function validateWhatsAppConsent({ enabled = false, consent = false } = {}) {
  if (!enabled) {
    return { valid: true, error: "" };
  }

  return {
    valid: Boolean(consent),
    error: consent ? "" : "WhatsApp reminders require consent.",
  };
}

export function buildWhatsAppReminderMessage(reminder = {}, { includeMedicationName = false } = {}) {
  const medicationName = sanitizeWhatsAppMedicationName(reminder.medication_name);

  if (includeMedicationName && medicationName) {
    return `Reminder: it's time for your medication: ${medicationName}.`;
  }

  return DEFAULT_WHATSAPP_REMINDER_MESSAGE;
}

export function messageIncludesDose(message = "", reminder = {}) {
  const dose = String(reminder.dose || "").trim();
  const text = String(message || "");
  if (DOSE_LIKE_PATTERN.test(text)) {
    DOSE_LIKE_PATTERN.lastIndex = 0;
    return true;
  }
  DOSE_LIKE_PATTERN.lastIndex = 0;
  if (!dose) return false;
  return text.toLowerCase().includes(dose.toLowerCase());
}

export function sanitizeWhatsAppMedicationName(value = "") {
  return String(value || "")
    .replace(DOSE_LIKE_PATTERN, "")
    .replace(/\s+/g, " ")
    .replace(/\s+([.,:;])/g, "$1")
    .trim();
}

export function channelIncludesWhatsApp(channel) {
  return channel === V2_REMINDER_CHANNELS.whatsapp || channel === V2_REMINDER_CHANNELS.both;
}

export function shouldSendWhatsAppReminder({ settings = {}, reminder = {} } = {}) {
  if (!settings.whatsapp_reminders_enabled) {
    return { ok: false, reason: "WhatsApp reminders are not enabled." };
  }

  if (reminder.active === false) {
    return { ok: false, reason: "Reminder is not active." };
  }

  if (!channelIncludesWhatsApp(reminder.channel || settings.channel)) {
    return { ok: false, reason: "Reminder channel does not include WhatsApp." };
  }

  const numberValidation = validateWhatsAppNumber(settings.whatsapp_number);
  if (!numberValidation.valid) {
    return { ok: false, reason: "WhatsApp reminders require consent and a valid WhatsApp number." };
  }

  const consentValidation = validateWhatsAppConsent({
    enabled: true,
    consent: settings.whatsapp_consent,
  });
  if (!consentValidation.valid) {
    return { ok: false, reason: "WhatsApp reminders require consent and a valid WhatsApp number." };
  }

  return { ok: true, reason: "", number: numberValidation.number };
}

export function createWhatsAppReminderPayload({ settings = {}, reminder = {}, scheduledFor = "", dryRun = false } = {}) {
  const decision = shouldSendWhatsAppReminder({ settings, reminder });
  if (!decision.ok) {
    return { ok: false, error: decision.reason, payload: null };
  }

  const message = buildWhatsAppReminderMessage(reminder, {
    includeMedicationName: settings.whatsapp_include_medication_name,
  });

  return {
    ok: true,
    error: "",
    payload: {
      to: decision.number,
      message,
      reminder_id: reminder.id || "",
      scheduled_for: scheduledFor,
      dry_run: Boolean(dryRun),
    },
  };
}

export function createWhatsAppFunctionBody({ settings = {}, reminder = {}, scheduledFor = "", dryRun = false } = {}) {
  const payloadResult = createWhatsAppReminderPayload({ settings, reminder, scheduledFor, dryRun });
  if (!payloadResult.ok) return payloadResult;

  return {
    ok: true,
    error: "",
    payload: {
      ...payloadResult.payload,
      number: payloadResult.payload.to,
      consent: Boolean(settings.whatsapp_consent),
      whatsapp_reminders_enabled: Boolean(settings.whatsapp_reminders_enabled),
      channel: reminder.channel || settings.channel,
    },
  };
}

export function maskWhatsAppNumberForDisplay(value = "") {
  const normalized = normalizeWhatsAppNumber(value);
  if (!normalized) return "";

  const prefix = normalized.startsWith("+") ? "+" : "";
  const digits = normalized.replace(/[^\d]/g, "");
  if (digits.length <= 4) return `${prefix}${digits}`;

  return `${prefix}${"*".repeat(Math.max(0, digits.length - 4))}${digits.slice(-4)}`;
}

export function validateWhatsAppReminderSettings(settings = {}) {
  const errors = {};
  const whatsappRequested =
    Boolean(settings.whatsapp_reminders_enabled) || channelIncludesWhatsApp(settings.channel);

  if (!whatsappRequested) {
    return { valid: true, errors, normalizedNumber: normalizeWhatsAppNumber(settings.whatsapp_number) };
  }

  const numberValidation = validateWhatsAppNumber(settings.whatsapp_number);
  if (!numberValidation.valid) {
    errors.whatsapp_number = "WhatsApp reminders require consent and a valid WhatsApp number.";
  }

  const consentValidation = validateWhatsAppConsent({
    enabled: true,
    consent: settings.whatsapp_consent,
  });
  if (!consentValidation.valid) {
    errors.whatsapp_consent = "WhatsApp reminders require consent and a valid WhatsApp number.";
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
    normalizedNumber: numberValidation.number,
  };
}

export function getWhatsAppReminderAttemptKey(reminderId = "", scheduledFor = "") {
  return `${String(reminderId || "").trim()}:${String(scheduledFor || "").trim()}`;
}

export function hasWhatsAppReminderAttempt(events = [], reminderId = "", scheduledFor = "") {
  const targetKey = getWhatsAppReminderAttemptKey(reminderId, scheduledFor);
  if (!targetKey || targetKey === ":") return false;

  return (Array.isArray(events) ? events : []).some((event) => {
    if (!event?.whatsapp_status) return false;
    return getWhatsAppReminderAttemptKey(event.reminder_id, event.scheduled_for) === targetKey;
  });
}
